import type { ICellRendererParams } from 'ag-grid-community'
import { isObjectType, isString } from 'remeda'
import { syncModalAccessibleName } from '../../modal'
import { createElement, setAttributes } from '../utils/dom'
import { createCellRenderer } from '../utils/renderer'

type ImageData = {
  readonly value: string
  readonly name: string
  readonly highlight?: unknown
}

type ImageDialog = HTMLElement & {
  open: boolean
}

type ActivePreview = {
  readonly dialog: ImageDialog
  readonly source: HTMLElement
  readonly owner?: HTMLElement
  readonly connectionObserver: MutationObserver
  readonly handleDialogAfterHide: EventListener
  readonly handleOwnerAfterHide: EventListener
}

let activePreview: ActivePreview | undefined

const cleanupPreview = (preview: ActivePreview): void => {
  preview.connectionObserver.disconnect()
  preview.dialog.removeEventListener(
    'wa-after-hide',
    preview.handleDialogAfterHide
  )
  preview.owner?.removeEventListener(
    'wa-after-hide',
    preview.handleOwnerAfterHide
  )
  preview.dialog.remove()

  if (activePreview === preview) {
    activePreview = undefined
  }
}

const closeActivePreview = (): void => {
  const preview = activePreview

  if (!preview) {
    return
  }

  preview.dialog.open = false
  cleanupPreview(preview)
}

const hasDisconnectedContext = (preview: ActivePreview): boolean =>
  !preview.source.isConnected ||
  (preview.owner !== undefined && !preview.owner.isConnected)

const closeDisconnectedPreview = (dialog: ImageDialog): void => {
  const preview = activePreview

  if (preview?.dialog === dialog && hasDisconnectedContext(preview)) {
    closeActivePreview()
  }
}

const isImageData = (value: unknown): value is ImageData =>
  isObjectType(value) &&
  value !== null &&
  'value' in value &&
  'name' in value &&
  isString(value.value) &&
  isString(value.name)

const createImageElement = (src: string, alt: string): HTMLImageElement =>
  createElement('img', (image: HTMLImageElement) => {
    setAttributes(image, {
      src,
      alt,
      class: 'w-7 h-7 rounded object-cover'
    })

    const handleError = (): void => {
      image.style.display = 'none'
    }

    image.addEventListener('error', handleError, { once: true })
  })

const createImageDialog = (imageUrl: string, filename: string): ImageDialog =>
  createElement<ImageDialog>('wa-dialog', (dialog) => {
    setAttributes(dialog, {
      label: filename,
      class: 'image-preview-dialog',
      'data-component': '.dialog',
      'light-dismiss': ''
    })

    const fullImage = createImageElement(imageUrl, filename)
    fullImage.classList.remove('w-7', 'h-7', 'rounded')
    fullImage.classList.add(
      'max-w-full',
      'max-h-[75vh]',
      'h-auto',
      'w-auto',
      'object-contain'
    )
    dialog.appendChild(fullImage)
  })

export const ImageComponent = createCellRenderer<ImageData>({
  validate: isImageData,

  render: (data: ImageData, _params: ICellRendererParams): HTMLElement => {
    if (!(data.value && data.name)) {
      return createElement('span', (element) => {
        element.classList.add('text-gray-400', 'text-xs')
        element.textContent = 'No image'
      })
    }

    return createElement('button', (button: HTMLButtonElement) => {
      setAttributes(button, {
        type: 'button',
        class:
          'inline-flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer',
        title: data.name
      })

      button.appendChild(createImageElement(data.value, data.name))

      const handleClick = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation()
        closeActivePreview()

        const dialog = createImageDialog(data.value, data.name)
        const owner = button.closest<HTMLElement>('wa-drawer') ?? undefined

        const handleDialogAfterHide = (hideEvent: Event): void => {
          const preview = activePreview

          if (hideEvent.target === dialog && preview?.dialog === dialog) {
            cleanupPreview(preview)
          }
        }

        const handleOwnerAfterHide = (hideEvent: Event): void => {
          const preview = activePreview

          if (hideEvent.target === owner && preview?.dialog === dialog) {
            closeActivePreview()
          }
        }

        const connectionObserver = new MutationObserver(() => {
          closeDisconnectedPreview(dialog)
        })

        const preview: ActivePreview = {
          dialog,
          source: button,
          owner,
          connectionObserver,
          handleDialogAfterHide,
          handleOwnerAfterHide
        }

        dialog.addEventListener('wa-after-hide', handleDialogAfterHide)
        owner?.addEventListener('wa-after-hide', handleOwnerAfterHide)
        document.body.appendChild(dialog)
        syncModalAccessibleName(dialog)
        activePreview = preview
        connectionObserver.observe(document.body, {
          childList: true,
          subtree: true
        })
        dialog.open = true
      }

      button.addEventListener('click', handleClick)
    })
  },

  wrapperClasses: ['flex', 'items-center', 'justify-center']
})

export type { ImageData }
